/**
 * The outcome tracker. After a press, the request is with the processor: the affected appointment and
 * day show a non-blocking Pending badge, the tracker polls the row (quickly for the first half-minute, then
 * every 15 s; every 60 s after five minutes) until ProcessingStatus leaves Pending / Claimed / In Progress, then announces the processor's
 * OutcomeMessage verbatim — in a toast and on the row — and keeps it in Recent requests (the last 20,
 * on this device). With the mock driver the tracker runs the processors itself on the first poll
 * (POST /api/dev/tick), so a round trip takes a second in tests.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TxSubmitResponse, TxView } from '@shared/api-types';
import type { DevicePress } from '@shared/with-scheduler';
import { apiFetch } from './api';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { useOutcomeGloss } from '@/components/OutcomeGloss';

export interface TrackedTx extends TxView {
  /** What the person pressed, in their words ("Book TEST-03C · Tue 6"). */
  label: string;
  unit?: string;
  /** The appointment and day the Pending badge sits on. */
  badgeAppointmentId?: string | null;
  badgeDayKey?: string | null;
  mock?: boolean;
  trackedAt: number;
  polls: number;
}

interface TxCtx {
  tracked: TrackedTx[];
  track: (res: Pick<TxSubmitResponse, 'transaction' | 'steps' | 'mock'>, meta: { label: string; unit?: string; appointmentId?: string | null; dayKey?: string | null }) => void;
  pendingFor: (appointmentId: string | null | undefined) => TrackedTx | null;
  pendingForDay: (dayKey: string | null | undefined) => TrackedTx[];
  lastFor: (appointmentId: string | null | undefined) => TrackedTx | null;
}

const Ctx = createContext<TxCtx | null>(null);

/** The tracked requests as the "with the scheduler" merge reads them (shared/with-scheduler.ts). */
export const devicePresses = (tracked: readonly TrackedTx[]): DevicePress[] =>
  tracked.map((x) => ({ queue: x.queue, operation: x.operation, transactionId: x.transactionId, appointmentId: x.badgeAppointmentId, open: x.open, submittedAt: x.submittedAt, trackedAt: x.trackedAt }));

/** Queue 1, 2 and 3 rows are numbered separately: a tracked request is known by its queue and its id. */
export const txKey = (x: Pick<TxView, 'id' | 'queue'>): string => `${x.queue ?? 'q1'}:${x.id}`;
const KEY = 'abf.recentRequests';
const MAX = 20;
export const POLL_MS = 15_000;
export const SLOW_POLL_MS = 60_000;
export const SLOW_AFTER_MS = 5 * 60_000;

/**
 * 14 Oct 2026 ("I press, and then I'm waiting"): a request still open when the press was answered is usually only a
 * few seconds from closed (the engine goes on after the press's budget), so the first polls come quickly — 1, 1.5,
 * 2, 3, 5 and 8 s after the press — and only then every 15 s. One poll is one read of one row.
 */
export const FIRST_POLLS_MS: readonly number[] = [1000, 1500, 2000, 3000, 5000, 8000];

/** When the next poll is due: quickly at first, then 15 s, then 60 s once the request is five minutes old (the mock: at once). */
export function nextPollDelay(tx: Pick<TrackedTx, 'trackedAt' | 'polls' | 'mock'>, now = Date.now()): number {
  if (tx.mock) return tx.polls === 0 ? 400 : 1500;
  if (now - tx.trackedAt >= SLOW_AFTER_MS) return SLOW_POLL_MS;
  return FIRST_POLLS_MS[tx.polls] ?? POLL_MS;
}

function load(): TrackedTx[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as TrackedTx[]).slice(0, MAX) : [];
  } catch {
    return [];
  }
}

export function TxProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const gloss = useOutcomeGloss();
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const [tracked, setTracked] = useState<TrackedTx[]>(load);
  const timers = useRef(new Map<string, number>());
  const ticked = useRef(false);
  const trackedRef = useRef(tracked);
  trackedRef.current = tracked;

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(tracked.slice(0, MAX)));
    } catch {
      /* private mode: the list lives for this page only */
    }
  }, [tracked]);

  const refreshReads = useCallback(() => {
    for (const k of ['today', 'attention', 'appointments', 'slots', 'tenant', 'tenant-history', 'tenant-stats', 'booking-days', 'booking-preview', 'queued', 'completions', 'closeout', 'planning', 'slotadmin']) {
      void qc.invalidateQueries({ queryKey: [k] });
    }
  }, [qc]);

  const closed = useCallback(
    (tx: TrackedTx, refresh = true) => {
      const words = tx.outcomeMessage ?? t('tx.closedNoMessage', { status: tx.processingStatus });
      const g = gloss(tx.outcomeCode);
      const line = `${tx.label}: ${words}${g ? ` — ${g}` : ''}`;
      toast(line);
      announce(line, tx.outcomeCode === 'Applied' || tx.outcomeCode === 'Duplicate Ignored' ? 'polite' : 'assertive');
      if (refresh) refreshReads();
    },
    [announce, gloss, refreshReads, t, toast],
  );

  const poll = useCallback(
    async (key: string) => {
      timers.current.delete(key);
      const cur = trackedRef.current.find((x) => txKey(x) === key);
      if (!cur || !cur.open) return;
      try {
        if (cur.mock && !ticked.current) {
          ticked.current = true;
          await apiFetch('/api/dev/tick', { method: 'POST' });
          ticked.current = false;
        }
        const v = await apiFetch<TxView>(cur.queue === 'q3' ? `/api/completions/tx/${cur.id}` : cur.queue === 'q2' ? `/api/capacity/tx/${cur.id}` : `/api/transactions/${cur.id}`);
        setTracked((xs) => xs.map((x) => (txKey(x) === key ? { ...x, ...v, polls: x.polls + 1 } : x)));
        if (!v.open) closed({ ...cur, ...v });
      } catch {
        ticked.current = false;
        setTracked((xs) => xs.map((x) => (txKey(x) === key ? { ...x, polls: x.polls + 1 } : x)));
      }
    },
    [closed],
  );

  // One timer per open request.
  useEffect(() => {
    for (const tx of tracked) {
      const key = txKey(tx);
      if (!tx.open || timers.current.has(key)) continue;
      const h = window.setTimeout(() => void poll(key), nextPollDelay(tx));
      timers.current.set(key, h);
    }
  }, [tracked, poll]);

  useEffect(() => {
    const all = timers.current;
    return () => {
      for (const h of all.values()) window.clearTimeout(h);
      all.clear();
    };
  }, []);

  const track: TxCtx['track'] = useCallback((res, meta) => {
    const now = Date.now();
    const items: TrackedTx[] = res.steps.map((s) => ({
      ...s,
      label: s.id === res.transaction.id ? meta.label : `${meta.label} · ${t(`tx.op.${s.operation}`, { defaultValue: s.operation })}`,
      unit: meta.unit,
      badgeAppointmentId: s.id === res.transaction.id ? (meta.appointmentId ?? s.appointmentId) : s.appointmentId,
      badgeDayKey: meta.dayKey ?? s.dayKey,
      mock: res.mock,
      trackedAt: now,
      polls: 0,
    }));
    setTracked((xs) => [...items.reverse(), ...xs.filter((x) => !items.some((i) => txKey(i) === txKey(x)))].slice(0, MAX));
    // 1.6.0: the app's engine answered with the press — say the outcome now and re-read, there is nothing to poll for
    // 14 Oct 2026: a swap answers three requests at once — the day is read again once, not once per request
    // (each re-read cancels the one before it and starts the day's reads over)
    const done = items.filter((x) => !x.open);
    done.forEach((x, i) => closed(x, i === done.length - 1));
  }, [t, closed]);

  const value = useMemo<TxCtx>(
    () => ({
      tracked,
      track,
      pendingFor: (id) => (id ? (tracked.find((x) => x.open && x.badgeAppointmentId === id) ?? null) : null),
      pendingForDay: (day) => (day ? tracked.filter((x) => x.open && x.badgeDayKey === day) : []),
      lastFor: (id) => (id ? (tracked.find((x) => !x.open && x.badgeAppointmentId === id && Date.now() - x.trackedAt < 30 * 60_000) ?? null) : null),
    }),
    [tracked, track],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTx(): TxCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTx outside TxProvider');
  return v;
}
